<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="onClose"
  >
    <div class="w-full max-w-lg p-4 bg-white rounded-lg shadow-lg" @click.stop>
      <h3 class="mb-2 text-base font-semibold">
        <i class="mr-1 fa" :class="icon"></i> {{ title }}
      </h3>
      <p class="mb-3 text-sm text-slate-600" v-html="description"></p>

      <div
        class="dropzone"
        :class="{ 'dropzone--active': dragOver, 'dropzone--disabled': loading }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        @click="openFilePicker"
      >
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept=".csv,.xlsx,.xls"
          @change="onFilePicked"
          @click.stop
        />
        <i class="mb-2 text-2xl fa fa-file-excel text-teal-600"></i>
        <p class="text-sm font-medium text-slate-700">
          Tërhiqni skedarin këtu ose klikoni për të zgjedhur
        </p>
        <p class="mt-1 text-xs text-slate-500">CSV, XLSX, XLS · maks. 2000 kode</p>
        <p v-if="fileName" class="mt-2 text-xs font-medium text-teal-700">{{ fileName }}</p>
      </div>

      <div v-if="loading" class="py-4 text-sm text-center text-slate-500">
        <i class="mr-1 fa fa-spinner fa-spin"></i> Duke lexuar dhe gjetur produktet...
      </div>

      <div v-else-if="error" class="p-3 mt-3 text-sm text-red-700 rounded bg-red-50">
        {{ error }}
      </div>

      <div v-else-if="result" class="mt-3 space-y-2">
        <div class="p-3 text-sm rounded bg-slate-50 border border-slate-200">
          <p class="font-semibold text-slate-800">
            {{ result.matched.length }}
            {{ result.matched.length === 1 ? "produkt u gjet" : "produkte u gjetën" }}
          </p>
          <p v-if="result.missing.length" class="mt-1 text-amber-700">
            {{ result.missing.length }} kode nuk u gjetën
          </p>
        </div>

        <div v-if="result.missing.length" class="max-h-28 overflow-y-auto text-xs text-slate-600 border border-slate-100 rounded p-2">
          <span class="font-semibold text-slate-500">Mungojnë: </span>
          {{ result.missing.slice(0, 50).join(", ") }}
          <span v-if="result.missing.length > 50"> … (+{{ result.missing.length - 50 }})</span>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-4">
        <button type="button" class="px-3 py-1 text-sm border rounded" :disabled="loading" @click="onClose">
          Anulo
        </button>
        <button
          type="button"
          class="px-3 py-1 text-sm text-white bg-teal-600 rounded"
          :class="{ 'opacity-50 pointer-events-none': loading || !canContinue }"
          @click="onContinue"
        >
          <i class="mr-1 fa fa-arrow-right"></i> {{ continueLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ProductsServices from "@/services/Product";
import { parseProductCodesFromFile } from "@/utils/parseProductCodesFromFile";

export default {
  name: "LabelCodeImportModal",
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: "Importo etiketa nga skedar" },
    description: {
      type: String,
      default:
        'Ngarkoni një skedar CSV ose Excel me kode produktesh. Preferohet një kolonë <strong>code</strong> / <strong>kodi</strong>; përndryshe përdoret kolona e parë.',
    },
    continueLabel: { type: String, default: "Vazhdo me eksportin" },
    icon: { type: String, default: "fa-tags" },
  },
  emits: ["close", "resolved"],
  data() {
    return {
      dragOver: false,
      dragDepth: 0,
      loading: false,
      error: null,
      fileName: "",
      result: null,
    };
  },
  computed: {
    canContinue() {
      return (this.result?.guids || []).length > 0;
    },
  },
  watch: {
    open(value) {
      if (value) this.reset();
    },
  },
  methods: {
    reset() {
      this.dragOver = false;
      this.dragDepth = 0;
      this.loading = false;
      this.error = null;
      this.fileName = "";
      this.result = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onClose() {
      if (this.loading) return;
      this.$emit("close");
    },
    openFilePicker() {
      if (this.loading) return;
      this.$refs.fileInput?.click();
    },
    onDragEnter() {
      if (this.loading) return;
      this.dragDepth += 1;
      this.dragOver = true;
    },
    onDragOver() {
      if (this.loading) return;
      this.dragOver = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragOver = false;
    },
    onDrop(event) {
      if (this.loading) return;
      this.dragDepth = 0;
      this.dragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.processFile(file);
    },
    onFilePicked(event) {
      const file = event?.target?.files?.[0];
      if (file) this.processFile(file);
      if (event?.target) event.target.value = null;
    },
    async processFile(file) {
      this.loading = true;
      this.error = null;
      this.result = null;
      this.fileName = file?.name || "";
      try {
        const codes = await parseProductCodesFromFile(file);
        const data = await ProductsServices.resolveLabelCodes(codes);
        this.result = {
          guids: data?.guids || [],
          matched: data?.matched || [],
          missing: data?.missing || [],
        };
        if (!this.result.guids.length) {
          this.error = "Asnjë kod nga skedari nuk u gjet në katalog.";
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || "Gabim gjatë leximit të skedarit.";
      } finally {
        this.loading = false;
      }
    },
    onContinue() {
      if (!this.canContinue) return;
      this.$emit("resolved", {
        guids: [...this.result.guids],
        matched: [...this.result.matched],
        missing: [...this.result.missing],
      });
    },
  },
};
</script>

<style scoped>
.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s;
}

.dropzone:hover:not(.dropzone--disabled) {
  border-color: #14b8a6;
  background: #f0fdfa;
}

.dropzone--active {
  border-color: #0d9488;
  background: #ccfbf1;
}

.dropzone--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hidden {
  display: none;
}
</style>
